/**
 * Admin Shows Store
 * Manages podcast shows and podcast contract data for the admin podcast manager.
 *
 * Shows no longer carry a setup/active status. The only remaining distinction is
 * "needs_setup" — a virtual marker for companies that have a podcast contract but
 * no show document yet. These items are derived at runtime (no Firestore doc).
 * Real show documents are surfaced with a neutral "show" marker.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { showService } from '@/services/showService'
import { contractService } from '@/services/contractService'
import { episodeService } from '@/services/episodeService'
import { useAdminCompaniesStore } from '@/stores/adminCompaniesStore'
import { useAdminProjectsStore } from '@/stores/adminProjectsStore'
import { logger } from '@/services/loggerService'

export const useAdminShowsStore = defineStore('adminShows', () => {
  // ─── State ───────────────────────────────────────
  const shows = ref(new Map())
  const podcastContracts = ref([])
  const initialized = ref(false)
  const isLoading = ref(false)
  let unsubscribeShows = null

  // ─── Getters ─────────────────────────────────────
  const allShows = computed(() => Array.from(shows.value.values()))

  /**
   * Companies that have an active podcast contract but no show record yet.
   * These become virtual "needs_setup" items in the kanban.
   */
  const companiesNeedingSetup = computed(() => {
    const companiesWithShows = new Set(
      Array.from(shows.value.values()).map(s => s.companyId)
    )

    const companyMap = new Map()
    for (const contract of podcastContracts.value) {
      if (!contract.active) continue
      if (companiesWithShows.has(contract.companyId)) continue
      if (!companyMap.has(contract.companyId)) {
        companyMap.set(contract.companyId, {
          companyId: contract.companyId,
          contracts: []
        })
      }
      companyMap.get(contract.companyId).contracts.push(contract)
    }

    return Array.from(companyMap.values())
  })

  /**
   * Unified list for the main podcast grid.
   * Virtual "needs_setup" items (companies with a podcast contract but no show
   * yet) come first, followed by all real show documents (neutral "show"
   * marker). Every item is enriched with companyName.
   */
  const podcastListItems = computed(() => {
    const adminCompaniesStore = useAdminCompaniesStore()
    const getCompanyName = (companyId) => {
      const company = adminCompaniesStore.getCompanyById(companyId)
      return company?.name || 'Unknown Company'
    }

    // Virtual items for needs_setup (no Firestore doc)
    const needsSetupItems = companiesNeedingSetup.value.map(item => ({
      id: `setup-${item.companyId}`,
      isVirtual: true,
      status: 'needs_setup',
      companyId: item.companyId,
      companyName: getCompanyName(item.companyId),
      contracts: item.contracts
    }))

    const showItems = Array.from(shows.value.values()).map(show => ({
      ...show,
      status: 'show',
      companyName: getCompanyName(show.companyId)
    }))

    return [...needsSetupItems, ...showItems]
  })

  // ─── Actions ─────────────────────────────────────
  async function init() {
    if (initialized.value) return
    isLoading.value = true

    try {
      // Contracts are fetched once (they change infrequently)
      podcastContracts.value = await contractService.getContractsByPackageType('podcast')

      // Shows use real-time listener
      unsubscribeShows = showService.subscribeToShows((showsList) => {
        shows.value = new Map(showsList.map(s => [s.id, s]))
        isLoading.value = false
      })

      initialized.value = true

      logger.info('[AdminShowsStore] Initialized with real-time listener', {
        podcastContractCount: podcastContracts.value.length
      })
    } catch (error) {
      logger.error('[AdminShowsStore] Failed to initialize', { error: error.message })
      isLoading.value = false
      throw error
    }
  }

  function dispose() {
    if (unsubscribeShows) {
      unsubscribeShows()
      unsubscribeShows = null
    }
    initialized.value = false
  }

  async function createShow(showData) {
    try {
      const created = await showService.createShow(showData)
      logger.info('[AdminShowsStore] Show created', { showId: created.id })
      return created
    } catch (error) {
      logger.error('[AdminShowsStore] Failed to create show', { error: error.message })
      throw error
    }
  }

  async function updateShow(showId, updates) {
    try {
      await showService.updateShow(showId, updates)
      logger.info('[AdminShowsStore] Show updated', { showId })
    } catch (error) {
      logger.error('[AdminShowsStore] Failed to update show', { error: error.message })
      throw error
    }
  }

  async function deleteShow(showId) {
    try {
      await showService.deleteShow(showId)
      shows.value.delete(showId)
      logger.info('[AdminShowsStore] Show deleted', { showId })
    } catch (error) {
      logger.error('[AdminShowsStore] Failed to delete show', { error: error.message })
      throw error
    }
  }

  /**
   * What a show delete would remove, for the confirmation dialog.
   * Sums the per-episode counts the project cascade already reports.
   */
  async function getShowDeletionCounts(showId) {
    const projectsStore = useAdminProjectsStore()
    const episodes = await episodeService.getShowEpisodes(showId)

    const totals = {
      episodeCount: episodes.length,
      deliverableCount: 0,
      datedEventCount: 0,
      datelessEventCount: 0
    }

    for (const episode of episodes) {
      const counts = await projectsStore.getProjectDeletionCounts(episode.id)
      totals.deliverableCount += counts.deliverableCount
      totals.datedEventCount += counts.datedEventCount
      totals.datelessEventCount += counts.datelessEventCount
    }

    logger.info('[AdminShowsStore] Got show deletion counts', { showId, ...totals })
    return totals
  }

  /**
   * Delete a show plus every episode under it, reusing the project cascade so
   * deliverables, review data and calendar events get the same treatment they
   * get from a normal project delete — scheduled events are preserved as
   * historical records, unscheduled placeholders are removed.
   *
   * Episodes go first: a failure mid-loop leaves a show with fewer episodes,
   * which is re-runnable. Deleting the show first would strand its episodes
   * with no route back to them.
   */
  async function deleteShowWithCascade(showId) {
    const projectsStore = useAdminProjectsStore()
    const episodes = await episodeService.getShowEpisodes(showId)

    logger.info('[AdminShowsStore] Starting show cascade delete', {
      showId,
      episodeCount: episodes.length
    })

    const result = {
      deletedEpisodes: 0,
      deletedDeliverables: 0,
      preservedEvents: 0,
      deletedEvents: 0
    }

    for (const episode of episodes) {
      const episodeResult = await projectsStore.deleteProjectWithCascade(episode.id)
      result.deletedEpisodes++
      result.deletedDeliverables += episodeResult.deletedDeliverables || 0
      result.preservedEvents += episodeResult.preservedEvents || 0
      result.deletedEvents += episodeResult.deletedEvents || 0
    }

    await deleteShow(showId)

    logger.info('[AdminShowsStore] Show cascade delete complete', { showId, ...result })
    return { success: true, ...result }
  }

  function getShowById(showId) {
    return shows.value.get(showId) || null
  }

  return {
    // State
    shows,
    podcastContracts,
    initialized,
    isLoading,
    // Getters
    allShows,
    companiesNeedingSetup,
    podcastListItems,
    // Actions
    init,
    dispose,
    createShow,
    updateShow,
    deleteShow,
    getShowDeletionCounts,
    deleteShowWithCascade,
    getShowById
  }
})
